import { useEffect, useRef } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { ChartSettings } from '../components/SettingsModal'
import type { EarningsEvent } from '../types/earnings'
import {
  EARNINGS_MARKERS_NAME,
  type EarningsMarkersConfig,
} from '../utils/earningsMarkersIndicator'
import { CANDLE_PANE_ID } from '../types/chart'
import { sameInputs } from '../utils/sameInputs'

type Chart = ReturnType<ChartManager['getChart']>

/**
 * Mounts / updates / removes the EARNINGS_MARKERS system indicator on the
 * candle pane. Mirrors the session-indicator lifecycle in `useMarketSession`:
 * remove-first for a clean slate, then (re-)create with the earnings array +
 * session timezone in `extendData`. klinecharts has no "update extendData in
 * place" API for a figure-less indicator, so the effect re-creates on any
 * change of earnings / timezone / toggle — cheap (the badges redraw from
 * extendData every frame anyway).
 *
 * The indicator is left OFF when the toggle is off or no earnings are supplied,
 * so charts with no earnings data pay nothing.
 */
export function useEarningsMarkers(
  chartInstance: Chart | null,
  settings: ChartSettings,
  earnings: EarningsEvent[] | undefined,
): void {
  const showEarnings = settings.showEarnings
  const timezone = settings.timezone
  // Stable identity for the deps array without re-running on array reference
  // churn: the earnings payload is small, so a JSON key is fine here.
  const earningsKey = earnings ? JSON.stringify(earnings) : ''
  const appliedRef = useRef<unknown[] | null>(null)

  useEffect(() => {
    // A new earnings array with the same content must not re-create the indicator.
    const inputs = [chartInstance, showEarnings, timezone, earningsKey]
    if (sameInputs(appliedRef.current, inputs)) return
    appliedRef.current = inputs
    if (!chartInstance) return

    // Always remove first to ensure a clean state on any change.
    chartInstance.removeIndicator(CANDLE_PANE_ID, EARNINGS_MARKERS_NAME)

    if (showEarnings && earnings && earnings.length > 0) {
      chartInstance.createIndicator(
        {
          name: EARNINGS_MARKERS_NAME,
          extendData: {
            earnings,
            timezone,
          } satisfies EarningsMarkersConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [chartInstance, showEarnings, timezone, earningsKey, earnings])
}
